import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BootstrapTable from "react-bootstrap-table-next";
import ListAdminButtons from "../Common/ListAdminButtons";
import DeleteModal from "./DeleteModal";
import ResultModal from "../Common/ResultModal";
import SpinnerLoading from "../../Components/Common/SpinnerLoading";
import { languageServices } from "../../Services";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import Tooltip from "../Common/Tooltip";
import { AuthContext } from "../../Contexts/AuthContext";
import { roles } from "../../Config/Roles";

const Language = () => {
  const history = useHistory();
  const { t: translator } = useTranslation();
  const { updateLocTab, updateLocName, isMobile } = useContext(GlobalContext);
  const { roleId } = useContext(AuthContext);
  const { master, admin, support } = roles;
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  });
  const [resultModal, setResultModal] = useState({
    title: "",
    text: "",
    show: false,
    btnClass: "",
  });
  const [languages, setLanguages] = React.useState([]);
  const [filteredLanguages, setFilteredLanguages] = useState([]);
  const [update, setUpdate] = useState(new Date());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    name: "",
  });

  React.useEffect(() => {
    if (![master, admin, support].includes(Number(roleId)))
      history.push("/error-page/401");
    else {
      updateLocName("language");
      setIsLoading(true);
      languageServices
        .search({
          searchName: "",
          pageIndex: 0,
          pageSize: 0,
          sortName: "",
          isAsc: true,
        })
        .then((data) => {
          setIsLoading(false);
          setLanguages(data.data.data);
          setFilteredLanguages(data.data.data);
        })
        .catch(() => history.push("/Error"));
    }

    updateLocTab(0);
  }, [pagination, update]);

  React.useEffect(() => {
    if (languages) {
      setFilteredLanguages(
        languages.filter(
          (x) => x.name.toUpperCase().indexOf(filters.name.toUpperCase()) > -1
        )
      );
    }
  }, [filters, languages, update]);

  const handleToUpdate = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/edit-language/${selectedItems}`);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const confirmDelete = () => {
    let deletePromises = [];

    selectedItems.forEach((languageId) => {
      deletePromises.push(languageServices.deleteLanguage(languageId));
    });
    setSelectedItems([]);
    Promise.all(deletePromises)
      .then((res) => {
        setShowDeleteModal(false);

        const hasErrors = res.some((x) => !x.data.success);
        !hasErrors
          ? setResultModal({
              show: true,
              title: translator("Language-SuccessDeleteModal-Title"),
              text: translator("Language-SuccessDeleteModal-Text"),
              btnClass: "btn btn-primary",
            })
          : setResultModal({
              show: true,
              title: translator("Language-UnsuccessDeleteModal-Title"),
              text: translator("Language-UnsuccessDeleteModal-Text"),
              btnClass: "btn btn-primary",
            });
        setUpdate(new Date());
      })
      .catch((err) =>
        setResultModal({
          show: true,
          title: translator("Language-UnsuccessDeleteModal-Title"),
          text: translator("Language-UnsuccessDeleteModal-Text"),
          btnClass: "btn btn-primary",
        })
      );
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems([...selectedItems, row.languageId]);
    } else {
      setSelectedItems(selectedItems.filter((p) => p !== row.languageId));
    }
  };

  const handleOnSelectAll = (isSelect) => {
    if (isSelect) {
      setSelectedItems(languages.map((x) => x.languageId));
    } else {
      setSelectedItems([]);
    }
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setPagination({
      ...pagination,
      pageIndex: page - 1,
      pageSize: sizePerPage,
    });
    setUpdate(new Date());
  };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      if (e.target.cellIndex) {
        history.push(`/edit-language/${row.languageId}`);
      }
    },
  };

  const handleCloseResultModal = () => {
    setResultModal({ show: false });
  };

  const RemoteAll = ({ data, page, sizePerPage, totalSize, onTableChange }) => (
    <div>
      <BootstrapTable
        remote
        keyField="languageId"
        data={data}
        columns={columns}
        rowEvents={rowEvents}
        noDataIndication={translator("No-Data")}
        rowClasses="boostrap-table-row"
        onTableChange={onTableChange}
      />
    </div>
  );

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={selectedItems.find((x) => x === row.languageId)}
            onChange={(e) => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function actionHeaderFormatter(cell, rows) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={
              languages &&
              languages.length > 0 &&
              !languages
                .map((p) => p.languageId)
                .some((p) => selectedItems.indexOf(p) < 0)
            }
            onChange={(e) => handleOnSelectAll(e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  const columns = [
    {
      dataField: "languageId",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "50px", textAlign: "center" };
      },
      headerFormatter: actionHeaderFormatter,
      formatter: actionFormatter,
    },
    {
      dataField: "name",
      text: translator("Language-View-TableHeader-Name"),
      sort: true,
    },
    {
      dataField: "shortName",
      text: translator("Language-View-TableHeader-Code"),
      sort: true,
      hidden: Boolean(isMobile),
    },
    {
      dataField: "cultureCode",
      text: translator("EditLanguage-CultureCode"),
      sort: true,
      hidden: Boolean(isMobile),
    },
  ];

  const handleAddLanguage = () => {
    history.push("/add-language");
  };

  return (
    <div className="p-2">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("Language-View-Span-LanguageView")}</span>
          <button
            className={
              "btn btn-primary float-right " + (isMobile ? "btn-sm" : "")
            }
            onClick={handleAddLanguage}
          >
            <span
              className="icon icon-cancel web-hidden"
              style={{ transform: "rotate(45deg)" }}
            ></span>{" "}
            <span className="mob-hidden">
              {translator("Language-AddNewLAnguage")}
            </span>
          </button>
        </div>
        <div className="card-body">
          <fieldset className="scheduler-border">
            <legend className="scheduler-border">
              {translator("Language-View-Filters")}
            </legend>
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="form-group col-lg-3 col-md-4 col-xs-12">
                <label>{translator("Language-View-FiltersName")}</label>{" "}
                <Tooltip text={translator("LanguageTooltip-Msg")} />
                <input
                  name="name"
                  type="text"
                  className="form-control"
                  defaultValue={filters.name}
                  onChange={handleInputChange}
                />
              </div>
            </form>
          </fieldset>
          <ListAdminButtons
            selectedItemsLength={selectedItems.length}
            handleToUpdate={handleToUpdate}
            handleDelete={() => setShowDeleteModal(true)}
          />
          {isLoading && <SpinnerLoading />}
          {!isLoading && languages && (
            <RemoteAll
              data={filteredLanguages}
              page={pagination.pageIndex + 1}
              sizePerPage={pagination.pageSize}
              totalSize={pagination.totalCount}
              onTableChange={handleTableChange}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("Language-View-DeleteModal-Title")}
        text={translator("Language-View-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
      <ResultModal
        show={resultModal.show}
        title={resultModal.title}
        text={resultModal.text}
        btnClass={resultModal.btnClass}
        handleClose={handleCloseResultModal}
      />
    </div>
  );
};
export default Language;
